import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Select, Button } from "@repo/o37-ui";
import { ChevronLeft, ExternalLink, Settings } from "lucide-react";
import { FundraisingFund, FUND_STAGES, FUND_PRIORITIES } from '../../types';
import FundOverview from './FundOverview';
import FundActivity from './FundActivity';
import FundVDRActivity from './FundVDRActivity';
import FundModal from '../../components/Fundraising/FundModal';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

function TabButton({ active, onClick, children }: { active: boolean, onClick: () => void, children: React.ReactNode }) {
  return (
      <button
          onClick={onClick}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              active 
                  ? 'bg-neutral-100 text-neutral-900' 
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
          }`}
      >
          {children}
      </button>
  );
}

export default function FundDetail() {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  
  const [fund, setFund] = useState<FundraisingFund | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'activity' | 'vdr-activity'>('overview');
  const [showEditModal, setShowEditModal] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (fundId) {
      loadFund();
    }
  }, [fundId]);

  const loadFund = async () => {
    try {
      // First get the fund to find its fundraise_id
      const searchResponse = await fetch(`${API_URL}/fundraising/raises`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      const raises = await searchResponse.json();
      
      // Find which raise contains this fund
      for (const raise of raises) {
        const fundsResponse = await fetch(`${API_URL}/fundraising/raises/${raise.id}/funds`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        });
        const funds = await fundsResponse.json();
        const foundFund = funds.find((f: any) => f.id === fundId);
        
        if (foundFund) {
          // Now get full fund details
          const detailResponse = await fetch(
            `${API_URL}/fundraising/raises/${raise.id}/funds/${fundId}`,
            { headers: { 'x-user-id': userId, 'x-user-role': userRole } }
          );
          
          if (detailResponse.ok) {
            const data = await detailResponse.json();
            setFund(transformFund(data));
          }
          break;
        }
      }
    } catch (e) {
      console.error('Failed to load fund:', e);
    } finally {
      setLoading(false);
    }
  };

  const transformFund = (data: any): FundraisingFund => ({
    id: data.id,
    fundraiseId: data.fundraise_id,
    name: data.name,
    website: data.website,
    logoUrl: data.logo_url,
    thesis: data.thesis,
    typicalTicketMin: data.typical_ticket_min,
    typicalTicketMax: data.typical_ticket_max,
    portfolioCompanies: data.portfolio_companies,
    stage: data.stage,
    priority: data.priority,
    notes: data.notes,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    contacts: data.fundraising_contacts?.map((c: any) => ({
      id: c.id,
      fundId: c.fund_id,
      name: c.name,
      title: c.title,
      email: c.email,
      phone: c.phone,
      linkedinUrl: c.linkedin_url,
      isPrimary: c.is_primary,
      notes: c.notes,
      createdAt: c.created_at,
      updatedAt: c.updated_at
    })),
    termSheets: data.fundraising_term_sheets?.map((ts: any) => ({
      id: ts.id,
      fundId: ts.fund_id,
      investmentAmount: ts.investment_amount,
      preMoneyValuation: ts.pre_money_valuation,
      liquidationPreference: ts.liquidation_preference,
      participationCap: ts.participation_cap,
      boardSeats: ts.board_seats,
      proRataRights: ts.pro_rata_rights,
      antiDilution: ts.anti_dilution,
      status: ts.status,
      receivedAt: ts.received_at,
      expiresAt: ts.expires_at,
      documentUrl: ts.document_url,
      notes: ts.notes,
      createdAt: ts.created_at,
      updatedAt: ts.updated_at
    })),
    activities: data.fundraising_activities?.map((a: any) => ({
      id: a.id,
      fundId: a.fund_id,
      contactId: a.contact_id,
      type: a.type,
      content: a.content,
      metadata: a.metadata,
      createdBy: a.created_by,
      createdAt: a.created_at
    }))
  });

  const handleStageChange = async (newStage: string) => {
    if (!fund) return;
    
    try {
      await fetch(`${API_URL}/fundraising/raises/${fund.fundraiseId}/funds/${fund.id}/stage`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ stage: newStage })
      });
      loadFund();
    } catch (e) {
      console.error('Failed to update stage:', e);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (!fund) {
    return <div className="p-8">Fund not found</div>;
  }

  const stageConfig = FUND_STAGES.find(s => s.stage === fund.stage);
  const priorityConfig = FUND_PRIORITIES.find(p => p.priority === fund.priority);
  
  return (
    <div className="min-h-full pb-12">
      {/* Sticky Header */}
      <div className="border-b border-neutral-200 bg-white sticky top-0 z-10">
        <div className="mx-auto w-full px-4">
          <div className="flex h-16 items-center gap-4">
            <button 
              onClick={() => navigate('/fundraising/raises')}
              className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="h-6 w-px bg-neutral-200" />

            <div className="flex items-center gap-3">
              {fund.logoUrl ? (
                <img src={fund.logoUrl} alt={fund.name} className="w-8 h-8 rounded-lg object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center text-sm font-medium text-neutral-500">
                  {fund.name.charAt(0)}
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  {fund.name}
                  {fund.website && (
                    <a href={fund.website} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 text-neutral-400 hover:text-blue-600" />
                    </a>
                  )}
                </span>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-3">
               {/* Priority Badge */}
               <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${priorityConfig?.color || 'bg-neutral-400'}`} />
                  {priorityConfig?.label || fund.priority}
                </span>

               {/* Stage Select */}
               <Select
                  value={fund.stage}
                  onChange={(e) => handleStageChange(e.target.value)}
                  className={`text-xs py-1 h-8 ${stageConfig?.bgColor || 'bg-neutral-50'}`}
                >
                  {FUND_STAGES.map(({ stage, label }) => (
                    <option key={stage} value={stage}>{label}</option>
                  ))}
                  <option value="uninterested">Uninterested</option>
                </Select>

               {/* Settings/Edit Button */}
               <Button
                 variant="ghost"
                 onClick={() => setShowEditModal(true)}
                 className="h-8 w-8 p-0"
                 icon={<Settings className="w-4 h-4" />}
               />
            </div>

            <div className="h-6 w-px bg-neutral-200 mx-2" />

            {/* Navigation Tabs */}
            <div className="flex items-center gap-1">
              <TabButton 
                active={activeTab === 'overview'} 
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </TabButton>
              <TabButton 
                active={activeTab === 'activity'} 
                onClick={() => setActiveTab('activity')}
              >
                Activity
              </TabButton>
              <TabButton 
                active={activeTab === 'vdr-activity'} 
                onClick={() => setActiveTab('vdr-activity')}
              >
                VDR Activity
              </TabButton>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 space-y-8 mt-8">
        {activeTab === 'overview' && <FundOverview fund={fund} onUpdate={loadFund} />}
        {activeTab === 'activity' && <FundActivity fund={fund} onUpdate={loadFund} />}
        {activeTab === 'vdr-activity' && <FundVDRActivity fund={fund} />}
      </div>

      {/* Edit Fund Modal */}
      {showEditModal && fund && (
        <FundModal
          fundraiseId={fund.fundraiseId}
          fund={fund}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false);
            loadFund();
          }}
        />
      )}
    </div>
  );
}
